/**
 * Orders.tsx — customer orders page, Glass + Aurora language.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { orderService, Order } from '../../services/orderService';
import DashShell from '../../components/dash/DashShell';
import GlassCard from '../../components/dash/GlassCard';
import Pill, { pillForStatus } from '../../components/dash/Pill';

const FILTERS = ['all', 'pending', 'processing', 'completed', 'cancelled'] as const;
type FilterKey = typeof FILTERS[number];

const Orders: React.FC = () => {
  const { user, customer, loading: authLoading } = useAuth() as any;
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (user && customer) fetchOrders();
    // eslint-disable-next-line
  }, [user, customer]);

  const fetchOrders = async () => {
    setLoading(true); setError('');
    try {
      const r = await orderService.getMyOrders(100, 0);
      if (r.success && r.data) setOrders(r.data);
      else setError(r.error || 'Failed to load orders');
    } catch (e: any) { setError(e.message || 'Network error'); }
    finally { setLoading(false); }
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const fmtMoney = (n: number, currency = 'USD') => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currency || 'USD',
  }).format(n);

  const orderTypeLabel = (o: Order): string => {
    if ((o as any).package_id === 'domain-only' && o.domain_name) return `Domain · ${o.domain_name}`;
    if (o.domain_name) return `Hosting + ${o.domain_name}`;
    return 'Service Package';
  };

  const filtered = orders.filter(o => filter === 'all' ? true : o.status === filter);

  return (
    <DashShell
      title="Orders"
      subtitle="Every purchase tied to your account."
      right={
        <div className="glass-pill text-slate-700">
          <span className="text-slate-500">total</span>
          <strong className="text-slate-900">{orders.length}</strong>
        </div>
      }
    >
      {error && (
        <GlassCard delay={60} tint="rose" className="px-4 py-3 text-sm text-rose-700 mb-6">{error}</GlassCard>
      )}

      {/* Filter pills */}
      <GlassCard delay={120} variant="flat" className="px-4 py-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-lg transition ${
                filter === f
                  ? 'bg-gradient-to-b from-orange-500 to-orange-600 text-white shadow-sm'
                  : 'bg-white/60 text-slate-600 border border-slate-200/60 hover:bg-orange-50 hover:text-orange-700'
              }`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
          <span className="ml-auto self-center text-xs text-slate-500">
            {filtered.length} of {orders.length}
          </span>
        </div>
      </GlassCard>

      <GlassCard delay={180}>
        {loading ? (
          <div className="p-8 space-y-3">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse flex items-center gap-4">
                <div className="h-4 bg-slate-200/70 rounded w-32" />
                <div className="h-4 bg-slate-200/70 rounded w-48" />
                <div className="h-4 bg-slate-200/70 rounded w-20" />
                <div className="h-4 bg-slate-200/70 rounded w-24 ml-auto" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-slate-900">No orders found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {filter === 'all' ? "You haven't placed any orders yet." : `No ${filter} orders.`}
            </p>
            <a href="/hosting"
               className="inline-flex items-center mt-5 px-4 py-2 bg-gradient-to-b from-orange-500 to-orange-600 text-white rounded-lg text-sm hover:from-orange-600 hover:to-orange-700 shadow-sm transition">
              Browse services
            </a>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200/60">
                  <th className="py-3 px-5">Order</th>
                  <th className="py-3 px-3">Service</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3">Payment</th>
                  <th className="py-3 px-3 text-right">Amount</th>
                  <th className="py-3 px-3">Date</th>
                  <th className="py-3 px-5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => {
                  const oP = pillForStatus(o.status);
                  const pP = pillForStatus(o.payment_status);
                  return (
                    <tr key={o.id} className="border-b border-slate-200/40 hover:bg-slate-50/50 transition">
                      <td className="py-3 px-5 font-mono text-slate-900">#{o.order_number}</td>
                      <td className="py-3 px-3 text-slate-700">{orderTypeLabel(o)}</td>
                      <td className="py-3 px-3"><Pill label={oP.label} tone={oP.tone} /></td>
                      <td className="py-3 px-3"><Pill label={pP.label} tone={pP.tone} /></td>
                      <td className="py-3 px-3 text-right text-slate-900 font-semibold">
                        {fmtMoney(Number(o.total_amount), (o as any).currency)}
                      </td>
                      <td className="py-3 px-3 text-slate-500">{fmtDate(o.created_at)}</td>
                      <td className="py-3 px-5 text-right">
                        <button onClick={() => setSelectedOrder(o)}
                                className="text-xs text-slate-700 hover:text-orange-600 transition">
                          Details →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Order detail modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          {/* Backdrop blur */}
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" />
          {/* Card */}
          <div onClick={e => e.stopPropagation()} className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <GlassCard variant="flat" className="p-6">
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200/60">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Order</div>
                  <h3 className="text-xl font-semibold text-slate-900 font-mono">#{selectedOrder.order_number}</h3>
                </div>
                <button onClick={() => setSelectedOrder(null)}
                        className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <DetailItem label="Order status">
                  {(() => { const p = pillForStatus(selectedOrder.status); return <Pill label={p.label} tone={p.tone} size="sm" />; })()}
                </DetailItem>
                <DetailItem label="Payment status">
                  {(() => { const p = pillForStatus(selectedOrder.payment_status); return <Pill label={p.label} tone={p.tone} size="sm" />; })()}
                </DetailItem>
                <DetailItem label="Total amount">
                  <span className="text-lg font-semibold text-slate-900">
                    {fmtMoney(Number(selectedOrder.total_amount), (selectedOrder as any).currency)}
                  </span>
                </DetailItem>
                <DetailItem label="Currency">
                  <span className="text-sm text-slate-700">{(selectedOrder as any).currency || 'USD'}</span>
                </DetailItem>
                <DetailItem label="Order date">
                  <span className="text-sm text-slate-700">{fmtDate(selectedOrder.created_at)}</span>
                </DetailItem>
                <DetailItem label="Last updated">
                  <span className="text-sm text-slate-700">{fmtDate((selectedOrder as any).updated_at)}</span>
                </DetailItem>
              </div>

              <div className="mt-5 pt-5 border-t border-slate-200/60">
                <DetailItem label="Service">
                  <span className="text-sm text-slate-700">{orderTypeLabel(selectedOrder)}</span>
                  {(selectedOrder as any).billing_cycle && (
                    <div className="text-xs text-slate-500 mt-1 capitalize">{(selectedOrder as any).billing_cycle}</div>
                  )}
                </DetailItem>
              </div>

              {(selectedOrder as any).payment_method && (
                <div className="mt-4">
                  <DetailItem label="Payment method">
                    <span className="text-sm text-slate-700 capitalize">{(selectedOrder as any).payment_method}</span>
                  </DetailItem>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button onClick={() => setSelectedOrder(null)}
                        className="text-sm px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition">
                  Close
                </button>
              </div>
            </GlassCard>
          </div>
        </div>
      )}
    </DashShell>
  );
};

const DetailItem: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
    <div>{children}</div>
  </div>
);

export default Orders;
